// A component that renders a list of tasks.
// fetchItems is a function that fetches the next page of tasks. You can use useTaskQuery to get this function.
// onTaskClicked is a callback function that is called when a task is clicked. It is passed the task that was clicked.
// If onTaskClicked is not supplied, the task is navigated to when the user clicks it.
// assigneeLinkFor, when supplied, is `(task) => url`: each row's assigned name
// becomes a link to that url (the "tasks assigned to this person" page). Absent,
// the name is plain text and every other caller's tree is unchanged.
//
// PHOTOS. `showPhotos` + `photosByEntry` (a Map keyed by task id, from
// useTaskPhotos) + `onOpenPhoto` put a thumbnail strip under each row that has
// one. All three default to absent, so a caller passing none renders exactly
// the tree it rendered before -- the logbook table's rule, and its other
// callers' suites standing unedited is the proof.
//
// `showPhotos` and the hook's `enabled` are ONE boolean at the host, so "we
// fetched" and "we render" cannot disagree; the map being null (not asked) and
// empty (asked, nothing there) are different states and the strip draws
// neither, which is why nothing here has to distinguish them.


import Table from 'react-bootstrap/Table';
import { TaskListHeader } from './TaskListHeader';
import { TaskLine } from './TaskLine';
import { TaskCard } from './TaskCard';
import { useMyInfiniteScroll } from '../../components/MyInfiniteScroll';
import { compareTasksOverdueFirst } from '../../fleet-shared/TaskBuckets.mjs';
import { useIsMdUp } from '../../utilities/useIsMdUp';

export const TaskTable = ({
  fetchItems,
  onTaskClicked,
  entries,
  setEntries,
  tasksListBucket,
  sortOverdueFirst = false,
  assigneeLinkFor,
  showPhotos = false,
  photosByEntry,
  onOpenPhoto,
}) => {
  // At md+ we render the original 5-column table; below md the same tasks
  // render as stacked `TaskCard`s so dense columns don't fight a phone-sized
  // viewport. Branching in JS (rather than CSS-toggling two parallel DOM
  // trees) avoids rendering the full task list twice — important once
  // infinite scroll has loaded hundreds of rows. Crossing the breakpoint on
  // resize will reset scroll position inside the infinite-scroll wrapper;
  // that's an acceptable tradeoff today.
  const isMdUp = useIsMdUp();

  // Display-time sort. Underlying `entries` keeps fetch order so pagination
  // (`useMyInfiniteScroll` appends new pages) stays straightforward; we only
  // re-order the rendered rows so they match the bucket badges (Overdue →
  // Soon → Not Due) when "Overdue First" is selected.
  //
  // DELIBERATELY NOT MEMOIZED -- the logbook table's stated rule, and this is
  // why it exists. `MyInfiniteScroll` reads this through a ref (`latest`), and
  // `Control`'s identity is stable, so React would never remount out of a
  // stale closure: a `useCallback` missing `photosByEntry` from its deps would
  // freeze the list on the photos it had at first render, with no error
  // anywhere. The upside of that stable identity is the one that matters here:
  // the map landing re-renders the table rather than remounting it, so
  // in-flight thumbnail fetches survive and rows paged in later find their
  // photos already in the map.
  const renderChildren = (items) => {
    const ordered = sortOverdueFirst
      ? [...items].sort(compareTasksOverdueFirst)
      : items;

    // Undefined rather than an empty array when the switch is off or the map
    // has not answered: the row renders no continuation row at all, which is
    // what keeps a caller passing none identical to before.
    const photosFor = (task) =>
      (showPhotos && photosByEntry ? photosByEntry.get(task.id) : undefined) || undefined;

    if (!isMdUp) {
      return (
        <div className="vstack gap-2">
          {ordered.map((task) => (
            <TaskCard
              task={task}
              key={task.id}
              onTaskClicked={onTaskClicked}
              tasksListBucket={tasksListBucket}
              assigneeLinkFor={assigneeLinkFor}
              photos={photosFor(task)}
              onOpenPhoto={onOpenPhoto}
            />
          ))}
        </div>
      );
    }

    return (
      <Table responsive hover className="align-middle mb-0">
        <TaskListHeader />
        <tbody>
          {ordered.map((task) => (
            <TaskLine
              task={task}
              key={task.id}
              onTaskClicked={onTaskClicked}
              tasksListBucket={tasksListBucket}
              assigneeLinkFor={assigneeLinkFor}
              photos={photosFor(task)}
              onOpenPhoto={onOpenPhoto}
            />
          ))}
        </tbody>
      </Table>
    );
  };

  const endMessage = () => (
    <div className="text-center text-body-secondary py-3 small">
      — End of tasks —
    </div>
  );

  const { Control } = useMyInfiniteScroll(
    renderChildren,
    fetchItems,
    entries,
    setEntries,
    endMessage
  );

  return (
    <div>
      <Control />
    </div>
  );
};
